import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Flag from "@/components/Flag";
import PhoneInput from "@/components/PhoneInput";
import EmailSettings from "./EmailSettings";

export default function Settings() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const { t } = useTranslation();
    const [hotel, setHotel] = useState(null);
    const [plans, setPlans] = useState([]);
    const [sub, setSub] = useState(null);
    const [tab, setTab] = useState("general");
    const [form, setForm] = useState({ name: "", contact_email: "", phone: "", country: "", city: "" });

    const load = async () => {
        if (user?.role === "super_admin" || !user?.hotel_id) return;
        const [h, p, s] = await Promise.all([
            api.get(`/hotels/${user.hotel_id}`),
            api.get("/billing/plans"),
            api.get("/billing/subscriptions"),
        ]);
        setHotel(h.data);
        setForm({ name: h.data.name, contact_email: h.data.contact_email, phone: h.data.phone || "", country: h.data.country || "", city: h.data.city || "" });
        setPlans(p.data);
        setSub(s.data[0] || null);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

    const save = async () => {
        try { await api.patch(`/hotels/${hotel.id}`, form); toast.success("Saved"); await load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const subscribe = async (plan_id) => {
        try { await api.post("/billing/subscribe", { plan_id }); toast.success("Subscription updated"); await load(); await refresh(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    if (!hotel) return <><Topbar title="Settings" /><div className="p-6 text-sm text-muted-foreground">Loading…</div></>;

    return (
        <>
            <Topbar title="Settings" subtitle={hotel.name} />
            <div className="flex-1 overflow-auto p-6">
                <div className="mb-4 flex gap-1 border-b border-border">
                    {[
                        ["general", t("settings.general")],
                        ["languages", t("settings.languages")],
                        ["door_locks", t("settings.door_locks", "Door locks")],
                        ["email", "Email"],
                        ["billing", t("settings.billing")],
                        ["whitelabel", t("settings.white_label")],
                        ["integrations", t("settings.integrations")],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setTab(k)}
                            className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            data-testid={`settings-tab-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "general" && (
                    <div className="akm-card max-w-xl p-5 space-y-3">
                        <Field label="Hotel name"><input className="akm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Contact email"><input className="akm-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="City"><input className="akm-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
                            <Field label="Country"><input className="akm-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
                        </div>
                        <Field label={t("common.phone")}><PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} defaultCountry={(form.country || "AL").toUpperCase().slice(0,2)} testid="settings-hotel-phone" /></Field>
                        <div className="pt-2"><button onClick={save} className="akm-btn-primary" data-testid="settings-save">{t("settings.save_changes")}</button></div>
                    </div>
                )}

                {tab === "languages" && <LanguagesTab />}
                {tab === "door_locks" && <DoorLocksTab />}
                {tab === "email" && <EmailSettings embedded />}

                {tab === "billing" && (
                    <div>
                        {sub && (
                            <div className="akm-card mb-4 p-4 max-w-xl">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="akm-section-title">Current subscription</div>
                                        <div className="mt-1 text-base font-semibold tracking-tight">
                                            {plans.find((p) => p.id === sub.plan_id)?.name || "—"}
                                        </div>
                                    </div>
                                    <StatusBadge status={sub.status} />
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            {plans.map((p) => (
                                <div key={p.id} className="akm-card p-5">
                                    <div className="akm-section-title">{p.name}</div>
                                    <div className="mt-2 flex items-baseline gap-1">
                                        <span className="text-2xl font-semibold tracking-tight">{formatCurrency(p.price_monthly, p.currency)}</span>
                                        <span className="text-[11px] text-muted-foreground">/ month</span>
                                    </div>
                                    <ul className="mt-3 space-y-1 text-[12px] text-muted-foreground">
                                        <li>Up to {p.max_properties} properties</li>
                                        <li>Up to {p.max_rooms} rooms</li>
                                        <li>Up to {p.max_users} users</li>
                                        <li>{p.modules.length} modules</li>
                                    </ul>
                                    <button
                                        onClick={() => subscribe(p.id)}
                                        disabled={sub?.plan_id === p.id}
                                        className={`mt-4 w-full ${sub?.plan_id === p.id ? "akm-btn-ghost" : "akm-btn-primary"}`}
                                        data-testid={`subscribe-${p.name.toLowerCase()}`}
                                    >
                                        {sub?.plan_id === p.id ? "Current plan" : "Switch to this plan"}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800">
                            <strong>Payments are MOCKED.</strong> Stripe integration will be wired in next iteration.
                        </div>
                    </div>
                )}

                {tab === "whitelabel" && (
                    <div className="akm-card max-w-xl p-5 space-y-3">
                        <div className="akm-section-title">White-label</div>
                        <p className="text-sm text-muted-foreground">Custom brand colors, logo and domain are available on the Enterprise plan.</p>
                        <Field label="Brand color"><input type="color" defaultValue="#ff7b02" className="h-9 w-20 rounded-md border border-border" /></Field>
                        <Field label="Custom domain"><input className="akm-input" placeholder="pms.yourhotel.com" /></Field>
                        <button className="akm-btn-ghost" disabled>Save (Enterprise only)</button>
                    </div>
                )}

                {tab === "integrations" && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {["Stripe", "Booking.com", "Airbnb", "Expedia", "Mailgun", "Twilio"].map((i) => (
                            <div key={i} className="akm-card p-4">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">{i}</div>
                                    <StatusBadge status="trial" label="Coming soon" />
                                </div>
                                <p className="mt-2 text-[12px] text-muted-foreground">Connect your {i} account to sync data.</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

function LanguagesTab() {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [myLang, setMyLang] = useState(i18n.language || "sq");

    const load = async () => {
        try { setData((await api.get("/i18n/hotel-languages")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (code) => {
        const set = new Set(data.enabled);
        if (set.has(code)) {
            if (set.size <= 1) return toast.error("At least one language required");
            set.delete(code);
        } else { set.add(code); }
        const enabled = Array.from(set);
        const defaultLang = enabled.includes(data.default) ? data.default : enabled[0];
        setData({ ...data, enabled, default: defaultLang });
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/i18n/hotel-languages", {
                enabled: data.enabled, default: data.default,
            });
            toast.success(t("settings.save_changes"));
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;

    const supportedForUser = data.supported.filter((l) => data.enabled.includes(l.code));

    const chooseMyLang = async (code) => {
        setMyLang(code);
        i18n.changeLanguage(code);
        localStorage.setItem("akm_lang", code);
        try { await api.put("/auth/preferences", { language: code }); } catch { /* best-effort */ }
        toast.success(t("settings.save_changes"));
    };

    return (
        <div className="akm-card max-w-2xl p-5 space-y-4" data-testid="settings-languages">
            <div>
                <div className="text-[13px] font-semibold tracking-tight">{t("settings.language_preferences")}</div>
                <div className="mt-1 text-[12px] text-muted-foreground">{t("settings.languages_help")}</div>
            </div>

            <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                    {t("settings.my_language", "My language")}
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {supportedForUser.map((lang) => {
                        const active = myLang === lang.code;
                        return (
                            <button key={lang.code} type="button"
                                    onClick={() => chooseMyLang(lang.code)}
                                    data-testid={`settings-my-lang-${lang.code}`}
                                    className={`flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors ${
                                        active
                                            ? "border-[#ff7b02] bg-[#ff7b02]/8"
                                            : "border-border hover:bg-muted/40"
                                    }`}>
                                <Flag country={lang.country} className="!h-4" />
                                <div className="flex-1">
                                    <div className="text-[13px] font-medium">{lang.label}</div>
                                    <div className="text-[10px] text-muted-foreground">{lang.english}</div>
                                </div>
                                {active && <span className="text-[10px] font-semibold text-[#ff7b02]">●</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                    {t("settings.available_languages")}
                </div>
                <div className="space-y-1.5">
                    {data.supported.map((lang) => {
                        const globalEnabled = data.global_enabled.includes(lang.code);
                        const checked = data.enabled.includes(lang.code);
                        return (
                            <label key={lang.code}
                                   className={`flex items-center gap-3 rounded-md border border-border px-3 py-2 ${
                                       !globalEnabled ? "opacity-40" : "cursor-pointer hover:bg-muted/40"
                                   }`}>
                                <input type="checkbox" checked={checked} disabled={!globalEnabled}
                                       onChange={() => toggle(lang.code)}
                                       data-testid={`settings-lang-${lang.code}`} />
                                <Flag country={lang.country} className="!h-4" />
                                <div className="flex-1">
                                    <div className="text-[13px] font-medium">{lang.label}</div>
                                    <div className="text-[10px] text-muted-foreground">{lang.english}</div>
                                </div>
                                {!globalEnabled && (
                                    <span className="text-[10px] text-muted-foreground">Disabled by platform</span>
                                )}
                            </label>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                    {t("settings.default_language")}
                </div>
                <select value={data.default}
                        onChange={(e) => setData({ ...data, default: e.target.value })}
                        className="akm-input text-[13px] max-w-xs"
                        data-testid="settings-default-language">
                    {data.enabled.map((c) => {
                        const lang = data.supported.find((l) => l.code === c);
                        return <option key={c} value={c}>{lang?.label || c}</option>;
                    })}
                </select>
            </div>

            <div className="pt-2">
                <button onClick={save} disabled={busy} className="akm-btn-primary"
                        data-testid="settings-languages-save">
                    {t("settings.save_changes")}
                </button>
            </div>
        </div>
    );
}


function DoorLocksTab() {
    const { t } = useTranslation();
    const [providers, setProviders] = useState([]);
    const [integ, setInteg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [form, setForm] = useState({ provider: "", card_type: "" });

    const load = async () => {
        try {
            const [p, i] = await Promise.all([
                api.get("/keys/providers"),
                api.get("/keys/integration"),
            ]);
            setProviders(p.data);
            setInteg(i.data);
            if (i.data?.provider && !form.provider) {
                setForm({ provider: i.data.provider, card_type: i.data.card_type || "" });
            } else if (!form.provider && p.data[0]) {
                setForm({ provider: p.data[0].code, card_type: p.data[0].supported_card_types[0].code });
            }
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Poll agent status every 5s while tab is open
    useEffect(() => {
        const t = setInterval(async () => {
            try { setInteg((await api.get("/keys/integration")).data); } catch { /* silent */ }
        }, 5000);
        return () => clearInterval(t);
    }, []);

    if (!integ) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;

    const enable = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/keys/integration/enable", form);
            setInteg(data);
            toast.success(t("settings.door_locks_enabled", "Door-lock integration enabled"));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const disable = async () => {
        if (!window.confirm(t("settings.door_locks_disable_confirm", "Disable door-lock integration? The agent will be disconnected."))) return;
        setBusy(true);
        try { setInteg((await api.post("/keys/integration/disable")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const rotate = async () => {
        if (!window.confirm(t("settings.door_locks_rotate_confirm", "Generate a new pair token? The old agent will disconnect."))) return;
        setBusy(true);
        try { setInteg((await api.post("/keys/integration/rotate-token")).data); toast.success(t("settings.door_locks_rotated", "Token rotated")); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const patch = async (patch) => {
        setBusy(true);
        try { setInteg((await api.patch("/keys/integration", patch)).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const provider = providers.find((p) => p.code === (integ.provider || form.provider));
    const wsUrl = `${window.location.origin.replace(/^http/, "ws")}/api/ws/key-agent/${integ.pair_token || "YOUR_TOKEN"}`;

    return (
        <div className="max-w-3xl space-y-4" data-testid="settings-door-locks">
            <div className="akm-card p-5">
                <div className="mb-3 flex items-start justify-between">
                    <div>
                        <div className="text-[13px] font-semibold tracking-tight">{t("settings.door_locks", "Door locks")}</div>
                        <div className="mt-1 text-[12px] text-muted-foreground">
                            {t("settings.door_locks_help", "Connect a physical keycard encoder in reception. Cards are written on check-in and cancelled on check-out.")}
                        </div>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        integ.enabled
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                            : "bg-muted text-muted-foreground dark:bg-slate-500/20 dark:text-slate-300"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${integ.enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {integ.enabled ? t("common.enabled", "Enabled") : t("common.disabled", "Disabled")}
                    </div>
                </div>

                {!integ.enabled ? (
                    <div className="space-y-3">
                        <Field label={t("settings.door_locks_provider", "Provider")}>
                            <select className="akm-input text-[13px]" value={form.provider}
                                    onChange={(e) => {
                                        const p = providers.find((x) => x.code === e.target.value);
                                        setForm({ provider: e.target.value, card_type: p?.supported_card_types?.[0]?.code || "" });
                                    }}
                                    data-testid="door-locks-provider">
                                {providers.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                            </select>
                        </Field>
                        {provider && (
                            <Field label={t("settings.door_locks_card_type", "Card type")}>
                                <select className="akm-input text-[13px]" value={form.card_type}
                                        onChange={(e) => setForm({ ...form, card_type: e.target.value })}
                                        data-testid="door-locks-card-type">
                                    {provider.supported_card_types.map((c) => (
                                        <option key={c.code} value={c.code}>{c.label}</option>
                                    ))}
                                </select>
                            </Field>
                        )}
                        <button onClick={enable} disabled={busy || !form.provider || !form.card_type}
                                className="akm-btn-primary" data-testid="door-locks-enable">
                            {t("settings.door_locks_enable", "Enable integration")}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("settings.door_locks_provider", "Provider")}</div>
                                <div className="mt-0.5 text-[13px] font-medium">{provider?.name || integ.provider}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("settings.door_locks_card_type", "Card type")}</div>
                                <select className="akm-input mt-0.5 h-8 text-[12px]"
                                        value={integ.card_type || ""} onChange={(e) => patch({ card_type: e.target.value })}
                                        data-testid="door-locks-card-type">
                                    {(provider?.supported_card_types || []).map((c) => (
                                        <option key={c.code} value={c.code}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                            <input type="checkbox" checked={integ.auto_issue_on_checkin}
                                   onChange={(e) => patch({ auto_issue_on_checkin: e.target.checked })}
                                   data-testid="door-locks-auto-issue" />
                            {t("settings.door_locks_auto_issue", "Automatically write card on check-in")}
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                            <input type="checkbox" checked={integ.auto_cancel_on_checkout}
                                   onChange={(e) => patch({ auto_cancel_on_checkout: e.target.checked })}
                                   data-testid="door-locks-auto-cancel" />
                            {t("settings.door_locks_auto_cancel", "Automatically cancel card on check-out")}
                        </label>

                        <div className="rounded-md border border-border bg-muted/30 p-3">
                            <div className="mb-1 flex items-center justify-between">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    {t("settings.door_locks_agent", "Reception PC agent")}
                                </div>
                                <div className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                                    integ.agent_status === "online" ? "text-emerald-600" : "text-muted-foreground"}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${integ.agent_status === "online" ? "bg-emerald-500" : "bg-slate-400"}`} />
                                    {integ.agent_status === "online" ? t("keys.agent_online", "Online") : t("keys.agent_offline", "Offline")}
                                </div>
                            </div>
                            {integ.agent_last_seen && (
                                <div className="mb-2 text-[11px] text-muted-foreground">
                                    {t("settings.door_locks_last_seen", "Last seen")}: {formatDateTime(integ.agent_last_seen)}
                                    {integ.agent_version && <> · v{integ.agent_version}</>}
                                </div>
                            )}
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("settings.door_locks_pair_token", "Pair token")}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5">
                                <code className="flex-1 truncate rounded-md border border-border bg-background px-2 py-1 font-mono text-[11px]">
                                    {showToken ? integ.pair_token : "•".repeat(28)}
                                </code>
                                <button type="button" onClick={() => setShowToken((s) => !s)}
                                        className="rounded-md border border-border bg-card px-2 py-1 text-[10px] font-medium hover:bg-muted/60">
                                    {showToken ? t("common.hide", "Hide") : t("common.show", "Show")}
                                </button>
                                <button type="button" onClick={() => { navigator.clipboard.writeText(integ.pair_token || ""); toast.success(t("common.copied", "Copied")); }}
                                        className="rounded-md border border-border bg-card px-2 py-1 text-[10px] font-medium hover:bg-muted/60">
                                    {t("common.copy", "Copy")}
                                </button>
                                <button type="button" onClick={rotate}
                                        className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
                                        data-testid="door-locks-rotate">
                                    {t("settings.door_locks_rotate", "Rotate")}
                                </button>
                            </div>
                            <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("settings.door_locks_ws_url", "Agent connection URL")}
                            </div>
                            <code className="mt-1 block truncate rounded-md border border-border bg-background px-2 py-1 font-mono text-[11px]">
                                {wsUrl}
                            </code>

                            <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-[11px] text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200">
                                <div className="font-semibold">{t("settings.door_locks_setup_title", "Setup on the reception PC (one time)")}</div>
                                <ol className="ml-4 mt-1 list-decimal space-y-0.5">
                                    <li>{t("settings.door_locks_setup_step1", "Connect the USB card encoder and install its driver.")}</li>
                                    <li>{t("settings.door_locks_setup_step2", "Run LockReg.exe once with the Authorization Card to register the SDK.")}</li>
                                    <li>{t("settings.door_locks_setup_step3", "Install the Akomoda Key Agent and paste the pair token + URL above.")}</li>
                                </ol>
                                <a href="/downloads/AkomodaKeyAgent_Windows.zip" download
                                   className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-orange-600"
                                   data-testid="door-locks-download-agent">
                                    ⬇ {t("settings.door_locks_download", "Download Windows Agent (ZIP, ~4 MB)")}
                                </a>
                            </div>
                        </div>

                        <button onClick={disable} disabled={busy}
                                className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-100"
                                data-testid="door-locks-disable">
                            {t("settings.door_locks_disable", "Disable integration")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
